// 2026-07-01: Phase 14b — NDT Inspections (NADCAP commodity module — AC7114)
// Captures non-destructive test reports: method, technique sheet, operator level/cert,
// indications, acceptance spec, QA disposition. Second Tier-1 commodity after Heat Treat.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

// NADCAP AC7114 methods
const METHODS = [
  { code: 'PT', label: 'PT — Liquid Penetrant' },
  { code: 'MT', label: 'MT — Magnetic Particle' },
  { code: 'RT', label: 'RT — Radiographic' },
  { code: 'UT', label: 'UT — Ultrasonic' },
  { code: 'ET', label: 'ET — Eddy Current' },
  { code: 'VT', label: 'VT — Visual' },
];
const LEVELS = ['Level I', 'Level II', 'Level III'];

const STATUS_INFO = {
  COMPLETE_OK:    { color: BR.green, bg: '#dcfce7', label: '✓ ACCEPTED', icon: <VerifiedIcon fontSize="small" /> },
  COMPLETE_HOLD:  { color: BR.amber, bg: '#fef3c7', label: '⚠ REVIEW', icon: <GavelIcon fontSize="small" /> },
  RUNNING:        { color: BR.orange, bg: '#fff7ed', label: '▶ IN PROGRESS', icon: <SearchIcon fontSize="small" /> },
  REJECTED:       { color: BR.red, bg: '#fee2e2', label: '✗ REJECTED', icon: <GavelIcon fontSize="small" /> },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const emptyRec = {
  id: '',
  report_no: '',
  method: '',
  technique_no: '',
  component_code: '',
  component_name: '',
  batch_lot: '',
  wo_numbers: [],
  coverage_pct: '100',
  qty_inspected: '',
  qty_rejected: '',
  operator_name: '',
  operator_level: '',
  cert_no: '',
  acceptance_spec: '',
  indications: '',
  inspected_ts: '',
  qa_signoff_by: '',
  qa_signoff_ts: '',
  status: 'RUNNING',
  notes: '',
};

export default function NdtInspections() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyRec);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'ndt_inspections');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    const out = { ALL: items.length, RUNNING: 0, COMPLETE_OK: 0, COMPLETE_HOLD: 0, REJECTED: 0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.report_no || '').toLowerCase().includes(q)
          || (i.method || '').toLowerCase().includes(q)
          || (i.component_code || '').toLowerCase().includes(q)
          || (i.component_name || '').toLowerCase().includes(q)
          || (i.batch_lot || '').toLowerCase().includes(q);
    }).sort((a, b) => (new Date(b.inspected_ts) - new Date(a.inspected_ts)));
  }, [items, filter, search]);

  function openAdd() { setDraft({ ...emptyRec, id: `ndt-${Date.now()}` }); setEditOpen(true); }
  function openEdit(item) { setDraft({ ...emptyRec, ...item }); setEditOpen(true); }

  async function save() {
    if (!draft.report_no || !draft.method) {
      Swal.fire({ icon: 'warning', title: 'Report # and method required', confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1 ? [...items, draft]
                                 : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ ndt_inspections: next }, customerId, 'SERVER_SCOPE');
      setItems(next); setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.report_no} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setSaving(false); }
  }

  async function signOff(item) {
    const methodLabel = (METHODS.find(m => m.code === item.method) || {}).label || item.method;
    const { value: form } = await Swal.fire({
      title: `QA sign-off · ${item.report_no}`,
      html: `
        <div style="text-align:left; font-size:13px;">
          <div style="background:#fff7ed; padding:10px; border-radius:6px; margin-bottom:12px;">
            <strong>Method:</strong> ${methodLabel}<br/>
            <strong>Component:</strong> ${item.component_code || '?'} ${item.component_name ? '· ' + item.component_name : ''}<br/>
            <strong>Batch/Lot:</strong> ${item.batch_lot || '—'} · <strong>Coverage:</strong> ${item.coverage_pct || '?'}%<br/>
            <strong>Operator:</strong> ${item.operator_name || '?'} (${item.operator_level || 'level ?'}, cert ${item.cert_no || '—'})<br/>
            <strong>Acceptance:</strong> ${item.acceptance_spec || 'NOT RECORDED'}<br/>
            <strong>Indications:</strong> ${item.indications || 'none recorded'}
          </div>
          <label style="font-weight:700; display:block; margin-bottom:6px;">Disposition</label>
          <select id="qa-disp" style="width:100%; padding:10px; font-size:14px; font-weight:600; border:1.5px solid #d1d5db; border-radius:6px;">
            <option value="COMPLETE_OK">✓ Accept — no rejectable indications</option>
            <option value="COMPLETE_HOLD">⚠ Review — needs Level III / review</option>
            <option value="REJECTED">✗ Reject — rejectable indication found</option>
          </select>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">QA name</label>
          <input id="qa-name" placeholder="Your name (sign-off)" style="width:100%; padding:9px 12px; font-size:14px; border:1.5px solid #d1d5db; border-radius:6px;"/>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">Note</label>
          <textarea id="qa-note" rows="2" style="width:100%; padding:8px; font-size:13px; border:1.5px solid #d1d5db; border-radius:6px; font-family:inherit;"></textarea>
        </div>
      `,
      icon: 'info', showCancelButton: true,
      confirmButtonText: 'Sign off', confirmButtonColor: BR.orange,
      cancelButtonText: 'Cancel', reverseButtons: true,
      preConfirm: () => {
        const disp = document.getElementById('qa-disp').value;
        const name = document.getElementById('qa-name').value.trim();
        const note = document.getElementById('qa-note').value.trim();
        if (!name) { Swal.showValidationMessage('QA name required for sign-off'); return false; }
        return { disp, name, note };
      },
    });
    if (!form) return;
    const next = items.map(i => i.id === item.id ? {
      ...i, status: form.disp,
      qa_signoff_by: form.name,
      qa_signoff_ts: new Date().toISOString(),
      notes: form.note ? `${i.notes || ''}\nQA: ${form.note}` : i.notes,
    } : i);
    await shiftadd({ ndt_inspections: next }, customerId, 'SERVER_SCOPE');
    setItems(next);
    Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                 icon: 'success', title: `Signed off by ${form.name}` });
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🔎 NDT Inspections
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Method · technique sheet · operator level &amp; cert · indications · QA sign-off · NADCAP AC7114
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Record NDT
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL', label: 'All reports', color: BR.dark },
          { key: 'RUNNING', label: 'In progress', color: BR.orange },
          { key: 'COMPLETE_OK', label: 'Accepted', color: BR.green },
          { key: 'COMPLETE_HOLD', label: 'Review', color: BR.amber },
          { key: 'REJECTED', label: 'Rejected', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
             placeholder="Search report #, method, component, batch/lot…"
             style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`,
                       borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No NDT reports {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Record NDT" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status','Report #','Method','Component','Batch/Lot','Cov','Operator','Cert','Inspected','QA','WO','' ].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.RUNNING;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.report_no}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 800, color: BR.blue }}>{i.method}</span>
                      {i.technique_no && <div style={{ fontSize: 10, color: BR.muted }}>{i.technique_no}</div>}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{i.component_code || '—'}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.component_name || ''}</div>
                    </td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.batch_lot || '—'}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700 }}>{i.coverage_pct ? `${i.coverage_pct}%` : '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <div>{i.operator_name || '—'}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.operator_level || ''}</div>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontSize: 11, color: BR.muted }}>{i.cert_no || '—'}</td>
                    <td style={{ padding: '9px 10px', fontSize: 11 }}>{fmtDate(i.inspected_ts)}</td>
                    <td style={{ padding: '9px 10px' }}>
                      {i.qa_signoff_by ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: BR.green }}>✓ {i.qa_signoff_by}</span>
                      ) : (
                        <button onClick={() => signOff(i)}
                                style={{ padding: '3px 8px', background: BR.orange, color: '#fff',
                                          border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                          Sign off
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {(i.wo_numbers || []).slice(0, 2).map(wo => (
                        <Chip key={wo} label={wo} size="small"
                              sx={{ height: 18, fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                     background: '#eff6ff', color: '#1e3a8a', border: '1px solid #93c5fd', marginRight: 0.3, marginBottom: 0.3 }} />
                      ))}
                      {(i.wo_numbers || []).length > 2 && (
                        <span style={{ fontSize: 10, color: BR.muted }}>+{i.wo_numbers.length - 2}</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#eff6ff', border: `1px solid #bfdbfe`, borderRadius: 6 }}>
        <strong style={{ color: '#1e3a8a' }}>AC7114 note:</strong> Every NDT report must be performed and interpreted by a certified operator (SNT-TC-1A / NAS410 Level II or III) against a released technique sheet, with 100% coverage on flight-critical features. Operator certification recency is cross-checked against Operator Qualifications; acceptance is per the drawing/spec callout. QA disposition is captured on the immutable audit trail.
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit NDT report` : 'Record new NDT inspection'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <TextField label="Report # *" size="small" fullWidth
                       value={draft.report_no}
                       onChange={e => setDraft({ ...draft, report_no: e.target.value })}
                       placeholder="NDT-2026-0042" />
            <TextField label="Method *" size="small" fullWidth select
                       value={draft.method}
                       onChange={e => setDraft({ ...draft, method: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {METHODS.map(m => <MenuItem key={m.code} value={m.code}>{m.label}</MenuItem>)}
            </TextField>
            <TextField label="Technique Sheet #" size="small" fullWidth
                       value={draft.technique_no}
                       onChange={e => setDraft({ ...draft, technique_no: e.target.value })}
                       placeholder="TS-PT-014" />
            <TextField label="Component Code" size="small" fullWidth
                       value={draft.component_code}
                       onChange={e => setDraft({ ...draft, component_code: e.target.value })} />
            <TextField label="Component Name" size="small" fullWidth
                       value={draft.component_name}
                       onChange={e => setDraft({ ...draft, component_name: e.target.value })} />
            <TextField label="Batch / Lot #" size="small" fullWidth
                       value={draft.batch_lot}
                       onChange={e => setDraft({ ...draft, batch_lot: e.target.value })} />
            <TextField label="WO Numbers (comma-sep)" size="small" fullWidth
                       value={(draft.wo_numbers || []).join(', ')}
                       onChange={e => setDraft({ ...draft, wo_numbers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            <TextField label="Coverage (%)" size="small" fullWidth type="number"
                       value={draft.coverage_pct}
                       onChange={e => setDraft({ ...draft, coverage_pct: e.target.value })} />
            <TextField label="Acceptance Spec" size="small" fullWidth
                       value={draft.acceptance_spec}
                       onChange={e => setDraft({ ...draft, acceptance_spec: e.target.value })}
                       placeholder="ASTM E1417 / drawing note 5" />
            <TextField label="Qty Inspected" size="small" fullWidth type="number"
                       value={draft.qty_inspected}
                       onChange={e => setDraft({ ...draft, qty_inspected: e.target.value })} />
            <TextField label="Qty Rejected" size="small" fullWidth type="number"
                       value={draft.qty_rejected}
                       onChange={e => setDraft({ ...draft, qty_rejected: e.target.value })} />
            <TextField label="Inspected Time" size="small" fullWidth type="datetime-local"
                       value={draft.inspected_ts ? draft.inspected_ts.slice(0, 16) : ''}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, inspected_ts: e.target.value })} />
            <TextField label="Operator" size="small" fullWidth
                       value={draft.operator_name}
                       onChange={e => setDraft({ ...draft, operator_name: e.target.value })} />
            <TextField label="Operator Level" size="small" fullWidth select
                       value={draft.operator_level}
                       onChange={e => setDraft({ ...draft, operator_level: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
            <TextField label="Operator Cert #" size="small" fullWidth
                       value={draft.cert_no}
                       onChange={e => setDraft({ ...draft, cert_no: e.target.value })} />
            <TextField label="Status" size="small" fullWidth select
                       value={draft.status}
                       onChange={e => setDraft({ ...draft, status: e.target.value })}>
              <MenuItem value="RUNNING">▶ In progress</MenuItem>
              <MenuItem value="COMPLETE_OK">✓ Accepted</MenuItem>
              <MenuItem value="COMPLETE_HOLD">⚠ Review</MenuItem>
              <MenuItem value="REJECTED">✗ Rejected</MenuItem>
            </TextField>
            <TextField label="Indications found" size="small" fullWidth multiline minRows={2}
                       value={draft.indications}
                       onChange={e => setDraft({ ...draft, indications: e.target.value })}
                       sx={{ gridColumn: '1 / span 3' }} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 3' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
